import { ActionIcon, Button, Divider, Group, Stack, Switch, Text, TextInput } from "@mantine/core"
import { IconCopy, IconPlus, IconTrash } from "@tabler/icons-react"
import { DEFAULT_SLOT, RiderScheduleSlot, SCHEDULE_DAYS } from "../domain/rider-schedule"

interface Props {
  value: RiderScheduleSlot[]
  onChange: (slots: RiderScheduleSlot[]) => void
}

/** Editor semanal del horario del despachador: un interruptor por día y uno o varios tramos por día. */
export function RiderScheduleEditor({ value, onChange }: Props) {
  const slotsOf = (dia: number) => value.map((slot, index) => ({ slot, index })).filter(({ slot }) => slot.dia === dia)

  const toggleDay = (dia: number, works: boolean) => {
    if (works) onChange([...value, { dia, ...DEFAULT_SLOT }])
    else onChange(value.filter((slot) => slot.dia !== dia))
  }

  const updateSlot = (index: number, patch: Partial<RiderScheduleSlot>) => onChange(value.map((slot, i) => (i === index ? { ...slot, ...patch } : slot)))
  const removeSlot = (index: number) => onChange(value.filter((_, i) => i !== index))
  const addSlot = (dia: number) => onChange([...value, { dia, ...DEFAULT_SLOT }])

  /** Copia los tramos de un día a todos los demás días que ya trabajan. */
  const copyToWorkingDays = (dia: number) => {
    const source = slotsOf(dia).map(({ slot }) => slot)
    const otherWorkingDays = [...new Set(value.map((slot) => slot.dia))].filter((d) => d !== dia)
    const copies = otherWorkingDays.flatMap((d) => source.map((slot) => ({ ...slot, dia: d })))
    onChange([...source, ...copies])
  }

  return (
    <Stack gap="xs">
      {SCHEDULE_DAYS.map(({ dia, label }) => {
        const daySlots = slotsOf(dia)
        const works = daySlots.length > 0
        return (
          <div key={dia}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Switch label={label} checked={works} onChange={(e) => toggleDay(dia, e.currentTarget.checked)} w={130} mt={6} />
              <Stack gap={6} style={{ flex: 1 }}>
                {works ? (
                  daySlots.map(({ slot, index }) => (
                    <Group key={index} gap="xs" wrap="nowrap">
                      <TextInput type="time" aria-label={`${label}: hora de inicio`} value={slot.desde} onChange={(e) => updateSlot(index, { desde: e.currentTarget.value })} style={{ flex: 1 }} />
                      <Text size="sm">a</Text>
                      <TextInput type="time" aria-label={`${label}: hora de fin`} value={slot.hasta} onChange={(e) => updateSlot(index, { hasta: e.currentTarget.value })} style={{ flex: 1 }} />
                      <ActionIcon variant="subtle" color="red.7" aria-label="Quitar tramo" onClick={() => removeSlot(index)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))
                ) : (
                  <Text size="sm" c="dimmed" mt={6}>
                    Día libre
                  </Text>
                )}
                {works && (
                  <Group gap="xs">
                    <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />} onClick={() => addSlot(dia)}>
                      Agregar tramo
                    </Button>
                    <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconCopy size={12} />} onClick={() => copyToWorkingDays(dia)}>
                      Copiar a los demás días
                    </Button>
                  </Group>
                )}
              </Stack>
            </Group>
            <Divider mt="xs" />
          </div>
        )
      })}
      <Text size="xs" c="dimmed">
        Hora de Lima. Si la hora de fin es menor que la de inicio, el tramo cruza la medianoche y termina al día siguiente.
      </Text>
    </Stack>
  )
}
